import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import ParticipantManager from './components/ParticipantManager';
import CostCalculator from './components/CostCalculator';
import ShoppingList from './components/ShoppingList';
import PlanningTimeline from './components/PlanningTimeline';
import ExpenseTracker from './components/ExpenseTracker';
import SocialCoordination from './components/SocialCoordination';

const BarbecuePlanning = () => {
  const [activeTab, setActiveTab] = useState('participants');
  const [barbecueData, setBarbecueData] = useState({
    name: '',
    date: '',
    time: '',
    location: '',
    matchId: null,
    participants: [],
    costs: {
      meat: 0,
      beverages: 0,
      sides: 0,
      equipment: 0,
      total: 0
    },
    shoppingList: [],
    timeline: [],
    expenses: [],
    settings: {
      pricePerPerson: 0,
      pixKey: '',
      organizer: 'Você'
    }
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if coming from match details with pre-filled data
    const state = location?.state;
    if (state?.matchData) {
      setBarbecueData((prev) => ({
        ...prev,
        name: `Churrasco - ${state?.matchData?.name}`,
        date: state?.matchData?.date,
        time: state?.matchData?.time,
        location: state?.matchData?.location,
        matchId: state?.matchData?.id,
        participants: state?.matchData?.participants || []
      }));
    }
  }, [location]);

  const tabs = [
  {
    id: 'participants',
    label: 'Participantes',
    icon: 'Users',
    description: 'Gerenciar confirmados'
  },
  {
    id: 'costs',
    label: 'Custos',
    icon: 'Calculator',
    description: 'Calcular valores'
  },
  {
    id: 'shopping',
    label: 'Compras',
    icon: 'ShoppingCart',
    description: 'Lista de itens'
  },
  {
    id: 'timeline',
    label: 'Cronograma',
    icon: 'Clock',
    description: 'Organizar tempo'
  },
  {
    id: 'expenses',
    label: 'Pagamentos',
    icon: 'CreditCard',
    description: 'Controlar gastos'
  },
  {
    id: 'social',
    label: 'Social',
    icon: 'MessageCircle',
    description: 'Coordenar grupo'
  }];


  const handleBarbecueUpdate = (field, value) => {
    setBarbecueData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBarbecue = async () => {
    try {
      // Simulate API call to save barbecue planning
      console.log('Saving barbecue:', barbecueData);

      // Show success message and navigate
      alert('Churrasco planejado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving barbecue:', error);
      alert('Erro ao salvar o planejamento do churrasco');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'participants':
        return (
          <ParticipantManager
            participants={barbecueData?.participants}
            onUpdate={(participants) => handleBarbecueUpdate('participants', participants)} />);


      case 'costs':
        return (
          <CostCalculator
            costs={barbecueData?.costs}
            participants={barbecueData?.participants}
            onUpdate={(costs) => handleBarbecueUpdate('costs', costs)} />);


      case 'shopping':
        return (
          <ShoppingList
            shoppingList={barbecueData?.shoppingList}
            participants={barbecueData?.participants}
            onUpdate={(list) => handleBarbecueUpdate('shoppingList', list)} />);


      case 'timeline':
        return (
          <PlanningTimeline
            timeline={barbecueData?.timeline}
            barbecueDate={barbecueData?.date}
            barbecueTime={barbecueData?.time}
            onUpdate={(timeline) => handleBarbecueUpdate('timeline', timeline)} />);


      case 'expenses':
        return (
          <ExpenseTracker
            expenses={barbecueData?.expenses}
            costs={barbecueData?.costs}
            participants={barbecueData?.participants}
            settings={barbecueData?.settings}
            onUpdate={(expenses) => handleBarbecueUpdate('expenses', expenses)}
            onSettingsUpdate={(settings) => handleBarbecueUpdate('settings', settings)} />);


      case 'social':
        return (
          <SocialCoordination
            barbecueData={barbecueData}
            onUpdate={handleBarbecueUpdate} />);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header Section */}
        <div className="bg-card rounded-lg border border-border p-6 mb-6 shadow-elevation-1">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="shrink-0">

                  <Icon name="ArrowLeft" size={20} />
                </Button>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Utensils" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-heading font-bold text-foreground">
                      Planejamento do Churrasco
                    </h1>
                    <p className="text-muted-foreground font-caption">Organize o churrasco perfeito para sua partida

                    </p>
                  </div>
                </div>
              </div>
              
              {/* Quick Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Input
                  label="Nome do Evento"
                  value={barbecueData?.name}
                  onChange={(e) => handleBarbecueUpdate('name', e?.target?.value)}
                  placeholder="Ex: Churrasco da Pelada" />

                <Input
                  label="Data"
                  type="date"
                  value={barbecueData?.date}
                  onChange={(e) => handleBarbecueUpdate('date', e?.target?.value)} />

                <Input
                  label="Horário"
                  type="time"
                  value={barbecueData?.time}
                  onChange={(e) => handleBarbecueUpdate('time', e?.target?.value)} />

              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}>

                Cancelar
              </Button>
              <Button
                onClick={handleSaveBarbecue}
                iconName="Save"
                iconPosition="left">

                Salvar Planejamento
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-card rounded-lg border border-border mb-6 shadow-elevation-1">
          {/* Desktop Tabs */}
          <div className="hidden lg:flex border-b border-border">
            {tabs?.map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab?.id ?
              'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`
              }>

                <Icon name={tab?.icon} size={18} />
                <div className="text-center">
                  <div>{tab?.label}</div>
                  <div className="text-xs font-caption text-muted-foreground">
                    {tab?.description}
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Mobile Tabs */}
          <div className="lg:hidden p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Menu" size={20} className="text-muted-foreground" />
              <span className="font-medium text-foreground">Seções</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground hover:bg-muted/70'}`
                }>

                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-lg border border-border p-4 shadow-elevation-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Participantes</p>
                <p className="text-xl font-bold text-foreground">
                  {barbecueData?.participants?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-lg border border-border p-4 shadow-elevation-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Custo Total</p>
                <p className="text-xl font-bold text-foreground">
                  R$ {barbecueData?.costs?.total?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-lg border border-border p-4 shadow-elevation-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="ShoppingCart" size={20} className="text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Itens Compras</p>
                <p className="text-xl font-bold text-foreground">
                  {barbecueData?.shoppingList?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-lg border border-border p-4 shadow-elevation-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tarefas</p>
                <p className="text-xl font-bold text-foreground">
                  {barbecueData?.timeline?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default BarbecuePlanning;